import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import type { MeResponse } from "@/lib/api";
import { useDismissable } from "@/lib/useDismissable";
import { describeHit, hitPath, recentFallback, searchTargets, type SearchHit } from "@/lib/search";

/**
 * S6 · Global search (UX v0.3 W2, §10 keyboard).
 *
 * ⌘K opens it from anywhere; Esc closes it and returns focus to where it came
 * from. Results are entitlement-filtered **by the API** — nothing here filters,
 * because a client that filtered would already have been sent the thing it is
 * hiding (BR-3).
 *
 * **Esc is an escape hatch, not the dismissal contract.** It used to be the
 * only way to close the panel: clicking the shell behind it, tabbing past it,
 * or navigating elsewhere all left the results hanging over the page, so
 * anyone who opened it by ⌘K reflex and then reached for the mouse was stuck
 * with a panel they had no obvious way out of. `useDismissable` now closes it
 * on a press outside, on focus leaving, on Esc, and on a route change, and
 * every one of those decisions is unit-tested in `lib/dismiss.ts`. The one
 * that matters is the press: it is bound to `pointerdown` and gated on
 * containment, because an ungated mousedown-outside handler unmounts the panel
 * before the browser resolves a click on a RESULT — making every result
 * unclickable by mouse while still working by keyboard.
 *
 * Built on the shadcn `Input` plus a listbox rather than the `Command`
 * component the UX draft names: `Command` needs `cmdk`, and ⌘K + arrow keys +
 * Esc is a handful of lines we already own. Deliberate deviation, recorded
 * here rather than left as a silent difference from the spec.
 *
 * **The field states that it is a field.** It used to take the shadcn default,
 * whose `border-input` resolves to `--ks-grey-4` — against the top bar's
 * `--ks-surface` that is a 1.15:1 border, where WCAG 1.4.11 asks for 3:1 on a
 * UI component. There was effectively no boundary, and the ⌘K hint lived inside
 * a 3.15:1 placeholder that vanished the moment anyone focused the field
 * (Damien, 2026-08-27: "the search bar isn't very visible at the top of the
 * page"). Now: a `ks-grey-1` border (3.15:1), a tinted fill, a leading icon,
 * and the chord in a persistent badge OUTSIDE the placeholder, so the one
 * thing that teaches the shortcut survives focus and typing.
 */
export function PortalSearch({ me }: { me: MeResponse | null }) {
  const [open, setOpen] = useState(false);
  // Same platform read as the rail toggle in TopBar — Mac shows the glyph,
  // everything else spells it. An unknown platform falls back to Ctrl.
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform ?? "");
  const chord = mac ? "\u2318K" : "Ctrl+K";
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const originRef = useRef<HTMLElement | null>(null);
  // Wraps the field AND the results panel — "inside" for every dismissal
  // check. Tabbing from the field to a result must not read as leaving.
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // One dismissal path, whatever the interaction was. Returning focus to
  // origin is what makes it a genuine cancel rather than a dead end the
  // keyboard has to climb out of — but only when focus is still INSIDE the
  // panel, or a Tab-away would be yanked straight back to where it started.
  const dismiss = useCallback(() => {
    setOpen(false);
    setQuery("");
    const container = containerRef.current;
    const active = document.activeElement;
    if (container && active instanceof Node && container.contains(active)) {
      originRef.current?.focus();
    }
  }, []);

  useDismissable(containerRef, open, dismiss, pathname);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        originRef.current = document.activeElement as HTMLElement | null;
        setOpen(true);
        // Focus after the panel exists, or the input is not there to take it.
        queueMicrotask(() => inputRef.current?.focus());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useQuery({
    queryKey: ["search", query],
    queryFn: ({ signal }) => searchTargets(query, signal),
    enabled: open && query.trim().length > 0,
  });

  // Empty query offers the person's own modules, from the nav tree already in
  // hand — no round trip, and entitlement-correct for free.
  const hits: SearchHit[] = query.trim()
    ? (results.data?.results ?? [])
    : me
      ? recentFallback(me)
      : [];

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <Search
        className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-ks-grey-1"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls="portal-search-results"
        placeholder="Search modules…"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        className="h-9 rounded-ks-md border border-ks-grey-1 bg-ks-grey-4 pr-14 pl-9 text-sm text-ks-ink placeholder:text-ks-grey-1 focus-visible:border-ks-purple focus-visible:ring-2 focus-visible:ring-ks-purple-3"
      />
      {!open && (
        <kbd className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 rounded border border-ks-grey-3 bg-ks-surface px-1.5 py-0.5 text-xs text-ks-grey-1">
          {chord}
        </kbd>
      )}

      {open && (
        <ul
          id="portal-search-results"
          role="listbox"
          className="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-ks-md border border-ks-grey-4 bg-ks-surface py-1 shadow-lg"
        >
          {hits.map((hit) => (
            <li key={`${hit.kind}:${hit.key}`} role="option" aria-selected={false}>
              <a
                href={hitPath(hit)}
                className="block px-3 py-2 hover:bg-ks-grey-4"
                onClick={dismiss}
              >
                <span className="block text-sm text-ks-ink">{hit.display_name}</span>
                {/* Where it lives — the line that makes the panel teach the IA
                    rather than only answer the query (UX S6 ideal). */}
                <span className="block text-xs text-ks-grey-1">{describeHit(hit)}</span>
              </a>
            </li>
          ))}

          {query.trim() && !results.isPending && hits.length === 0 && (
            <li className="px-3 py-2 text-sm text-ks-ink">
              {results.data?.failed
                ? // A fault and a fact must never read the same.
                  "We can't search right now."
                : `No module called “${query}”.`}{" "}
              <a href="/app" className="underline">
                Browse all areas
              </a>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
